import {
  createContext,
  type ReactNode,
  useCallback,
  useContext,
  useMemo,
  useRef,
  useState,
} from 'react';
import { cn } from '~/lib/cn';

type ToastTone = 'info' | 'success' | 'error';
type ToastItem = { id: number; text: string; tone: ToastTone };

const ToastContext = createContext<{ show: (text: string, tone?: ToastTone) => void } | null>(null);

export function useToast() {
  const ctx = useContext(ToastContext);
  // Outside the provider (isolated component tests), toasts are a no-op.
  return ctx ?? { show: () => {} };
}

/** Short confirmations and errors, announced politely. They leave on their own. */
export function ToastProvider({ children }: { children: ReactNode }) {
  const [items, setItems] = useState<ToastItem[]>([]);
  const next = useRef(1);
  const show = useCallback((text: string, tone: ToastTone = 'info') => {
    const id = next.current++;
    setItems((list) => [...list.slice(-2), { id, text, tone }]);
    setTimeout(
      () => setItems((list) => list.filter((t) => t.id !== id)),
      tone === 'error' ? 7000 : 4000,
    );
  }, []);
  const value = useMemo(() => ({ show }), [show]);
  return (
    <ToastContext.Provider value={value}>
      {children}
      <div
        aria-live="polite"
        role="status"
        className="pointer-events-none fixed bottom-5 left-1/2 z-50 flex -translate-x-1/2 flex-col items-center gap-2"
      >
        {items.map((t) => (
          <div
            key={t.id}
            className={cn(
              'pointer-events-auto max-w-[520px] rounded-lg px-4 py-2.5 text-14 font-semibold shadow-card [animation:aw-toast-in_.18s_ease-out]',
              t.tone === 'error'
                ? 'border border-red-ink bg-red-tint text-red-ink'
                : t.tone === 'success'
                  ? 'border border-green-line bg-green-tint text-green-ink'
                  : 'bg-ink text-white',
            )}
          >
            {t.text}
          </div>
        ))}
      </div>
    </ToastContext.Provider>
  );
}
